<template>
  <div class="variables">
    <ValueModelTextAutoComplete :selected-hook-id="selectedHookId" class="autocomplete" :chart-data="true" @item-selected="setHookId($event)" />
    <SelectButton v-if="showSelectButton" v-model="selectOption" :options="selectOptions"></SelectButton>
  </div>
  <div ref="sheetEl" />
</template>

<script setup>
import {computed, defineEmits, nextTick, onMounted, ref, watch} from "vue";
import jspreadsheet from "jspreadsheet";
import formula from "@jspreadsheet/formula-pro"
import "jspreadsheet/dist/jspreadsheet.css";
import "jsuites/dist/jsuites.css";
import ValueModelTextAutoComplete from '@/views/Editor/CanvasTool/components/ValueModelTextAutoComplete.vue'
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue.ts";
import {storeToRefs} from "pinia";
import {useGivingTreeStore, useWidgetStore} from "@/store/index.ts";
import SelectButton from "primevue/selectbutton";

const sheetEl = ref(null)
const emit = defineEmits(['updateData'])
const selectedCell = ref()
const spreadsheet = ref()

const { getActiveHookId, getComputedValue, getComputedTypeByHookId, getComputedPrecisionByHookId, getComputedTextByHookId, getValueByHookId, getNotesByHookId } = useComputedTreeValue()
const widgetStore = useWidgetStore()
const { selectedWidget } = storeToRefs(widgetStore)

const givingTreeStore = useGivingTreeStore()
const { updated, valueDriversByHookId } = storeToRefs(givingTreeStore)

const spreadsheetData = ref([])
const spreadsheetCols = ref([])
const selectedHookId = ref(null)

const showSelectButton = ref(false)
const selectOptions = ref([
  'Name',
  'Notes',
  'Value'
])

onMounted(() => {
  initializeSpreadsheetData();
  initializeSpreadsheetCols();

  jspreadsheet.setLicense('NTYzN2FkOGQ2NGU5YzliMGY4OGYzMjdhN2EyMWUxOTA4MTE5M2MwOTEyYWZhNmEyY2E1ZjNhNWI0YjI1OTEzYzUyYTBkY2JjMDE1ZThmOTAwNDcyYWJiODMwZWJlZjlhNTMyNDIxMTE4MWZhNDI0MWIxNmI5OThmNzhhZjNjNWQsZXlKamJHbGxiblJKWkNJNklqSmhaRGsyWTJNeE5tVTJNalV3TVRjek1qZGlPV0ZoT0RjM1ltSmtaR1ppTXpVek1tRTNNVGdpTENKdVlXMWxJam9pVW1GeGRXVnNJRWRoY21OcFlTSXNJbVJoZEdVaU9qRTNOVEF6TnpRd01EQXNJbVJ2YldGcGJpSTZXeUpzWlhabGNtRm5aWEJ2YVc1MExtTnZiU0lzSWpFeU55NHdMakF1TVRvNE9EZzRJaXdpTVRJM0xqQXVNQzR4SWl3aWQyVmlJaXdpYkc5allXeG9iM04wSWwwc0luQnNZVzRpT2lJek15SXNJbk5qYjNCbElqcGJJblkzSWl3aWRqZ2lMQ0oyT1NJc0luWXhNQ0lzSW5ZeE1TSXNJbVp2Y20xMWJHRWlMQ0ptYjNKdGN5SXNJbkpsYm1SbGNpSXNJbkJoY25ObGNpSXNJbWx0Y0c5eWRHVnlJaXdpZG1Gc2FXUmhkR2x2Ym5NaUxDSmpiMjF0Wlc1MGN5SXNJbk5sWVhKamFDSXNJbU5vWVhKMGN5SmRmUT09')
  jspreadsheet.setExtensions({ formula })
  formula.adjustPrecision = true

  setupSpreadsheet();

  if (!selectedWidget.value.jspreadsheet) {
    selectedWidget.value.jspreadsheet = { data: saveSpreadsheetDataAsKeyValue(spreadsheet.value[0]), cols: spreadsheet.value[0].cols }
  }

  updateChartHookIds()
})

const initializeSpreadsheetData = () => {
  if (selectedWidget.value.data.series.length === 0) vdChartDataSetup()
  spreadsheetData.value = selectedWidget.value.jspreadsheet ? handleSpreadsheetData() : selectedWidget.value.data.series
}

const initializeSpreadsheetCols = () => {
  const defaultCols = [{ title: ' ', width: 120 }, { title: 'Values', width: 80 }]
  spreadsheetCols.value = selectedWidget.value.jspreadsheet ? selectedWidget.value.jspreadsheet.cols : defaultCols
}

const dataType = computed(() => selectedWidget.value.options.dataType)

const vdChartDataSetup = () => {
  const data = formatValueDriverData()
  selectedWidget.value.data.series = data.seriesFormat
  nextTick(() => setBulkHookIds(data.jspreadsheetFormat))
}

watch(dataType, (newValue) => {
  if (newValue === 'custom') {
    selectedWidget.value.data.hookIds = []
    const data = [['Data Point 1', 1], ['Data Point 2', 2]]
    selectedWidget.value.data.series = data
    nextTick(() => setCustomCells(data))
  } else if ('value-drivers') {
    vdChartDataSetup()
  }
})

const formatValueDriverData = () => {
  const valueDrivers = valueDriversByHookId.value
  const formattedData = []

  Object.keys(valueDrivers).forEach((valueDriverHookId) => {
    if (valueDrivers[valueDriverHookId].enabled) {
      const name = valueDrivers[valueDriverHookId].node.valueDriver.Name ?? ''
      const value = valueDrivers[valueDriverHookId].node.valueDriver.Value ?? 0

      formattedData.push({
        hookId: valueDriverHookId,
        name,
        value
      })
    }
  })

  // sorting vd's by hookId before adding them to jspreadsheet & chart data
  formattedData.sort((a, b) => a.hookId.localeCompare(b.hookId))

  const jspreadsheetFormat = formattedData.map(({ hookId, name, value }) => [
    { name, hookId },
    { value, hookId }
  ])

  const seriesFormat = formattedData.map(({ name, value }) => [name, value])

  return {
    jspreadsheetFormat: jspreadsheetFormat.filter(item => item !== undefined),
    seriesFormat: seriesFormat.filter(item => item !== undefined)
  }
}

const setupSpreadsheet = () => {
  //TODO: support multiple worksheets
  const newColumns = spreadsheetCols.value
      .filter(column => column.title)
      .map(({ title, width, x, type }) => ({ title, width, x, type }))

  spreadsheet.value = jspreadsheet(sheetEl.value, {
    worksheets: [{
      minDimensions: [20, 20],
      data: spreadsheetData.value,
      columns: newColumns,
      columnDrag: true,
      columnResize: true,
      allowRenameColumn: true,
    }],
    oneditionstart(instance) {
      // If the cell is mapped to a model value, then editing the cell will break that connection
      if (spreadsheet.value && spreadsheet.value[0] && selectedCell.value) {
        const hookIds = selectedWidget.value.data.hookIds.filter(obj => obj.name === getCellName(selectedCell.value[0], selectedCell.value[1]))
        if (hookIds.length > 0) {
          spreadsheet.value[0].setProperty(selectedCell.value[0], selectedCell.value[1],{ type:'text' })
          const cell = spreadsheet.value[0].getCell(selectedCell.value[0], selectedCell.value[1])
          spreadsheet.value[0].setValue(cell, '')
        }
      }
    },
    onafterchanges(instance, records) {
      let deletionInCol0 = false
      let otherChanges = false

      records.forEach(({ x, y, oldValue, value }) => {
        if (value === '' || value === null) {
          if (x === 0) {
            deletionInCol0 = true
          }
        } else {
          otherChanges = true
        }
      })

      if (deletionInCol0 || (otherChanges && selectedCell.value && selectedCell.value[0] !== 0)) {
        removeHookId()
      }
      updateChartData(instance)
    },
    onselection(el, x1, y1, x2, y2) {
      if (x1 === x2 && y1 === y2) {
        selectedCell.value = [x1, y1];

        const cellName = jspreadsheet.helpers.getColumnNameFromCoords(x1, y1)
        const match = selectedWidget.value.data.hookIds.find(item => item.name === cellName)
        selectedHookId.value = match ? match.hookId : null

        showSelectButton.value = x1 === 0 && selectedHookId.value
      }
    },
    onresizecolumn(worksheet, column, width) {
      selectedWidget.value.jspreadsheet.cols[column].width = width;
    },
    onchangeheader(worksheet, column, newValue) {
      selectedWidget.value.jspreadsheet.cols[column].title = newValue;
    },
    ondeleterow(worksheet, rows) {
      removeHookIdByRow(rows)
      updateChartData(spreadsheet.value[0])
    },
    ondeletecolumn(worksheet, column) {
      removeHookIdByColumn(column)
      updateChartData(spreadsheet.value[0])
    }
  })

  formatCells()
  window.test = spreadsheet.value[0]
}

const getCellName = (row, col) => {
  const columnLetter = String.fromCharCode(65 + row)
  return `${columnLetter}${col + 1}`
}

const findHookIdMatch = (row, col) => {
  return selectedWidget.value.data.hookIds.find(obj => obj.name === getCellName(row, col))
}

const updateCellValue = (value) => {
  const spreadsheetInstance = spreadsheet.value[0]
  const col = selectedCell.value[0]
  const row = selectedCell.value[1]

  spreadsheetInstance.setProperty(col, row, { type: 'text', mask: null })
  const cell = spreadsheetInstance.getCell(col, row)
  spreadsheetInstance.setValue(cell, value)
}

const getFirstColumnValue = (match, hookId) => {
  const computedText = getComputedTextByHookId(hookId)
  const values = {
    'Notes': getNotesByHookId(hookId),
    'Value': getValueByHookId(hookId),
    'Name': computedText !== '<Model Text>' ? computedText : ''
  }

  return values[match.selector]
}

const selectOption = computed({
  get: () => {
    const match = findHookIdMatch(selectedCell.value[0], selectedCell.value[1])
    return match && match.selector ? match.selector : ''
  },
  set: (val) => {
    const match = findHookIdMatch(selectedCell.value[0], selectedCell.value[1])
    if (!match) return

    match.selector = val
    const cellValue = getFirstColumnValue(match, match.hookId) ?? ' '

    updateCellValue(cellValue)
    updateChartData(spreadsheet.value[0])
  }
});

const formatCell = (row, col) => {
  if (col === 0) return
  const cellName = jspreadsheet.helpers.getColumnNameFromCoords(col, row)
  const match = selectedWidget.value.data.hookIds.find(item => item.name === cellName)
  if (match) {
    const hookId = match.hookId
    if (hookId) {
      const precision = getComputedPrecisionByHookId(hookId)
      const valueType = getComputedTypeByHookId(hookId)

      const precisionMask = precision > 0 ? '.' + '0'.repeat(precision) : ''
      if (valueType === 'currency') {
        spreadsheet.value[0].setProperty(col, row,{ type:'number',  mask: `$#,##0${precisionMask}` })
      }
      else if (valueType === 'percent') {
        spreadsheet.value[0].setProperty(col, row,{ type:'number', mask: `0${precisionMask}%` })
      }
      else {
        spreadsheet.value[0].setProperty(col, row,{ type:'number', mask: null })
      }
    }
  }
}

const formatCells = () => {
  if (selectedWidget.value.jspreadsheet) {
    for (const [key, value] of Object.entries(selectedWidget.value.jspreadsheet.data)) {
      //? - could this pose future problems?
      const col = key.charCodeAt(0) - 65 // Convert column letter to index
      const row = parseInt(key.slice(1)) - 1 // Convert row number to index
      nextTick(() => {
        formatCell(row, col)
      })

    }
  }
}

const handleSpreadsheetData = () => {
  const data = []

  for (const [key, value] of Object.entries(selectedWidget.value.jspreadsheet.data)) {
    //? - could this pose future problems?
    const col = key.charCodeAt(0) - 65 // Convert column letter to index
    const row = parseInt(key.slice(1)) - 1 // Convert row number to index

    if (!data[row]) {
      data[row] = []
    }
    data[row][col] = value
  }

  return data
}

const saveSpreadsheetDataAsKeyValue = (spreadsheet) => {
  const data = {}
  const columns = spreadsheet.options.columns
  const rows = spreadsheet.options.data.length

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns.length; col++) {
      if (spreadsheet.options.data[row]) {
        const cellRef = String.fromCharCode(65 + col) + (row + 1)
        data[cellRef] = spreadsheet.options.data[row][col]
      }
    }
  }
  return data
}

const setHookId = (event) => {
  const hookId = event.value.hookId
  if (selectedCell.value[0] === 0) {
    setFirstColumnCell(selectedCell.value[0], selectedCell.value[1], hookId)
  } else {
    setOtherColumnCell(selectedCell.value[0], selectedCell.value[1], hookId)
  }
  updateChartData(spreadsheet.value[0])
}

const setBulkHookIds = (data) => {
  const sheet = spreadsheet.value[0]
  sheet.setData([])

  //If there are no enabled value drivers on the VM set custom data
  if (!data || data.length === 0) {
    setCustomCells([['Value Driver 1', 1]])
    return
  }

  data.forEach((rowData, rowIndex) => {
    const [nameData, valueData] = rowData
    const firstCellCol = rowIndex
    const otherCellCol = rowIndex
    const firstCellRow = 0
    const otherCellRow = 1

    setFirstColumnCell(firstCellRow, firstCellCol, nameData.hookId)
    setOtherColumnCell(otherCellRow, otherCellCol, valueData.hookId)
  })

  updateChartData(sheet)
}

const setFirstColumnCell = (row, col, hookId) => {
  const cellName = getCellName(row, col)
  updateOrAddHookId(cellName, hookId)

  const match = findHookIdMatch(row, col)
  match.selector = 'Name'
  const cellValue = getFirstColumnValue(match, hookId) ?? ' '

  const sheet = spreadsheet.value[0]
  sheet.setProperty(row, col, { type: 'text' })

  const cell = sheet.getCell(row, col)
  sheet.setValue(cell, cellValue)
}

const setOtherColumnCell = (row, col, hookId) => {
  const value = getValueByHookId(hookId)
  const precision = getComputedPrecisionByHookId(hookId)
  const formattedValue = parseFloat(value.toFixed(precision))
  const valueType = getComputedTypeByHookId(hookId)

  const sheet = spreadsheet.value[0]

  sheet.setValueFromCoords(row, col, formattedValue)

  updateOrAddHookId(getCellName(row, col), hookId)
  setCellProperties(sheet, row, col, valueType, precision)
}

const setCellProperties = (sheet, row, col, valueType, precision) => {
  const precisionMask = precision > 0 ? '.' + '0'.repeat(precision) : ''

  const masks = {
    'currency': `$#,##0${precisionMask}`,
    'percent': `0${precisionMask}%`
  }

  sheet.setProperty(row, col, {
    type: 'number',
    mask: masks[valueType] ?? null
  })
}

const setCustomCells = (data) => {
  const sheet = spreadsheet.value[0]
  sheet.setData([])

  data.forEach((rowData, rowIndex) => {
    const [nameData, valueData] = rowData
    const firstCellCol = rowIndex
    const otherCellCol = rowIndex
    const firstCellRow = 0
    const otherCellRow = 1

    sheet.setProperty(firstCellRow, firstCellCol, { type: 'text' })
    const firstCell = sheet.getCell(firstCellRow, firstCellCol)
    sheet.setValue(firstCell, nameData ?? ' ')

    const formattedValue = parseFloat(valueData.toFixed(2))
    sheet.setValueFromCoords(otherCellRow, otherCellCol, formattedValue)
  })

  updateChartData(sheet)
}

const updateOrAddHookId = (cellName, hookId) => {
  const data = selectedWidget.value.data.hookIds
  const match = data.find(item => item.name === cellName)

  if (match) {
    match.hookId = hookId
  } else {
    data.push({ name: cellName, hookId: hookId })

  }
}

const removeHookId = () => {
  if (selectedWidget.value?.data.hookIds.length !== 0 && selectedCell.value) {
    selectedWidget.value.data.hookIds = selectedWidget.value.data.hookIds.filter(obj => obj.name !== getCellName(selectedCell.value[0], selectedCell.value[1]));
  }
}

const removeHookIdByRow = (rows) => {
  if (selectedWidget.value.data.hookIds.length !== 0) {
    rows.forEach(row => {
      const regex = new RegExp(`\\D${row + 1}$`);
      selectedWidget.value.data.hookIds = selectedWidget.value.data.hookIds.filter(obj => {
        return !regex.test(obj.name);
      })
    })
  }
}

const removeHookIdByColumn = (columns) => {
  if (selectedWidget.value.data.hookIds.length !== 0) {
    columns.forEach(column => {
      const col = String.fromCharCode(65 + column);
      const regex = new RegExp(`^${col}\\d+`);
      selectedWidget.value.data.hookIds = selectedWidget.value.data.hookIds.filter(obj => {
        return !regex.test(obj.name)
      })
    })
  }
}

const updateChartData = (spreadsheet) => {
  selectedWidget.value.jspreadsheet.data = saveSpreadsheetDataAsKeyValue(spreadsheet)
  const updatedData = handleSpreadsheetData()
  emit('updateData', updatedData)
}

const updateChartHookIds = () => {
  if (selectedWidget.value.data.hookIds.length === 0) return

  selectedWidget.value.data.hookIds.forEach(item => {
    const cellCoords = jspreadsheet.helpers.getCoordsFromCellName(item.name)

    if (cellCoords[0] === 0) return

    const value = getValueByHookId(item.hookId)
    const precision = getComputedPrecisionByHookId(item.hookId)
    const formattedValue = parseFloat(value?.toFixed(precision))

    spreadsheet.value[0].setValueFromCoords(cellCoords[0], cellCoords[1], formattedValue)
  })

  updateChartData(spreadsheet.value[0])
}

</script>

<style lang="scss" scoped>
:deep(.p-autocomplete-input) {
  height: 400px;
}

:deep(.jcontextmenu > div::before) {
  content: ''
}

.autocomplete {
  padding-bottom: 20px;
  position: sticky;
  top: 0;
  z-index: 99;
}

:deep(.auto-dropdown) {
  height: auto;
}

:deep(.p-autocomplete-dd .p-autocomplete-input) {
  height: auto;
  font-size: 14px;
}

:deep(.p-button.p-component.p-button-icon-only.p-autocomplete-dropdown.auto-dropdown) {
  background: transparent;
  color: #000;
  border-left: none;
  border-color: rgb(206, 212, 218);
}

.variables {
  display: flex;
  flex-direction: row;
  flex-direction: row;
  gap: 20px;
}

:deep(.p-selectbutton .p-button) {
  padding: 0.2rem !important;
  font-size: 0.9rem !important;
}
</style>
